import {
	CheckCircleFilled,
	CheckCircleOutlined,
	CloseOutlined,
	ExportOutlined,
} from '@ant-design/icons';
import { WithDialog } from '@kinderlabs-pos/ui-atoms';
import { TCAgreementContents, TermsConditionsType } from '@kinderlabs-pos/ui-components';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
	Button,
	Checkbox,
	CheckboxProps,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	IconButton,
	Radio,
	RadioGroup,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	styled,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { PrevNextButton } from '../../components/UserInfoPageComponents/PrevNextButton';
import { UserInfoPageTemplate } from './UserInfoPageTemplate';
import {
	GuestMemberInfoType,
	KioskInfoType,
	VisitUserGendersType,
} from '@kinderlabs-pos/shared-data-type';
import { getTelephoneNumberWithDash } from '@kinderlabs-pos/shared-util';
import { useState } from 'react';
import Grid2 from '@mui/material/Unstable_Grid2';

type AgreementType = {
	kinderlabsAgreement: boolean;
	storeAgreement: boolean;
	marketingAgreement: boolean;
	birthday?: string;
	gender?: VisitUserGendersType;
};

type TermsConditionsCheckProps = {
	agreement: AgreementType;
	setAgreement: (agreement: AgreementType) => void;
	birthday?: string;
	setBirthday: (birthday: string) => Promise<void>;
	gender?: VisitUserGendersType;
	setGender: (gender: VisitUserGendersType) => Promise<void>;
	goNextStep: () => void;
	goPrevStep: () => void;
};

export const UserTermsConditionsPage = ({
	props,
	kioskSignupInfo,
	telephone,
}: {
	props: TermsConditionsCheckProps;
	kioskSignupInfo: KioskInfoType['signupInfo'];
	telephone?: string;
}) => {
	const isBirthdayEnabled = kioskSignupInfo?.isBirthdayEnabled ?? false;
	const isGenderEnabled = kioskSignupInfo?.isGenderEnabled ?? false;

	const formik = useFormik<AgreementType & { birthday?: string; gender?: VisitUserGendersType }>({
		initialValues: {
			...props.agreement,
			birthday: props.birthday,
			gender: props.gender ?? 'MAN',
		},
		onSubmit: async (agreement) => {
			props.setAgreement(agreement);
			props.goNextStep();
		},
		validationSchema: Yup.object({
			kinderlabsAgreement: Yup.boolean().oneOf([true]),
			storeAgreement: Yup.boolean().oneOf([true]),
			birthday: Yup.string()
				.nullable()
				.min(7, '생년월일은 7자리 이상이어야 합니다.')
				.max(8, '생년월일은 8자를 넘을 수 없습니다.')
				.required('생년월일을 입력해주세요')
				.matches(
					/^(19[0-9][0-9]|20\d{2})(0[0-9]|1[0-2])(0[1-9]|[1-2][0-9]|3[0-1])$/,
					'생년월일 형식에 맞지 않습니다.'
				),
		}),
		validateOnMount: true,
		validateOnChange: true,
		enableReinitialize: true,
	});

	return (
		<UserInfoPageTemplate>
			<Stack
				flex={1}
				sx={{ width: '100%', pt: 3 }}
				spacing={2}>
				<Stack spacing={2}>
					<Stack
						width={'100%'}
						direction={'row'}
						alignItems={'center'}
						justifyContent={'space-between'}>
						<Typography>전화번호</Typography>
						<Typography>{telephone ? getTelephoneNumberWithDash(telephone) : ''}</Typography>
					</Stack>
					{isBirthdayEnabled && (
						<Stack
							width={'100%'}
							direction={'row'}
							alignItems={'center'}
							justifyContent={'space-between'}>
							<Typography>생년월일</Typography>
							<WithDialog
								dialog={(open, closeDialog) => (
									<Dialog
										open={open}
										onClose={closeDialog}
										maxWidth='md'
										fullWidth>
										<DialogTitle>생년월일 입력</DialogTitle>
										<Stack px={4}>생년월일 8자리를 입력하세요.</Stack>
										<Stack px={1}>
											<NumberPinDialogContent
												initialPinValues={formik.values.birthday || ''}
												errorMessage={formik.errors.birthday ? formik.errors.birthday : undefined}
												onSubmit={async (pinValues) => {
													// 값을 설정하고 validation 실행
													formik.setFieldValue('birthday', pinValues, true);

													// validation이 완료될 때까지 기다린 후 에러 확인
													const error = await formik.validateField('birthday');

													// 에러가 없으면 명시적으로 제거
													if (!error) {
														formik.setFieldError('birthday', undefined);
													}

													closeDialog();
												}}
												closeDialog={closeDialog}
											/>
										</Stack>
									</Dialog>
								)}>
								{(openDialog) => (
									<Stack
										spacing={1}
										sx={{ flex: 1, maxWidth: 380 }}>
										<Button
											variant='outlined'
											color={formik.errors.birthday ? 'error' : 'primary'}
											fullWidth
											onClick={openDialog}>
											<Typography>{formik.values.birthday || '생년월일을 입력하세요'}</Typography>
										</Button>
										{formik.touched.birthday && formik.errors.birthday && (
											<Typography
												variant='caption'
												color='error'
												sx={{ px: 1 }}>
												{formik.errors.birthday}
											</Typography>
										)}
										{(!formik.touched.birthday || !formik.errors.birthday) && (
											<Typography
												variant='caption'
												sx={{ px: 1, opacity: 0 }}>
												ㅤ
											</Typography>
										)}
									</Stack>
								)}
							</WithDialog>
						</Stack>
					)}
					{isGenderEnabled && (
						<Stack
							width={'100%'}
							direction={'row'}
							alignItems={'center'}
							justifyContent={'space-between'}>
							<Typography>성별</Typography>
							<RadioGroup
								row
								value={formik.values.gender}
								onChange={(e) =>
									formik.setValues((prev) => ({
										...prev,
										gender: e.target.value as VisitUserGendersType,
									}))
								}>
								<FormControlLabel
									value={'MAN' as GuestMemberInfoType['gender']}
									sx={{ flex: 1, justifyContent: 'center', mr: 8 }}
									control={
										<Radio
											onChange={(e) =>
												formik.setValues((prev) => ({
													...prev,
													gender: e.target.value as VisitUserGendersType,
												}))
											}
											name={'gender'}
											sx={{
												'& .MuiSvgIcon-root': {
													fontSize: 40,
												},
											}}
										/>
									}
									label={'남성'}
								/>
								<FormControlLabel
									value={'WOMAN' as GuestMemberInfoType['gender']}
									sx={{ flex: 1, justifyContent: 'center' }}
									control={
										<Radio
											onChange={(e) =>
												formik.setValues((prev) => ({
													...prev,
													gender: e.target.value as VisitUserGendersType,
												}))
											}
											name={'gender'}
											sx={{
												'& .MuiSvgIcon-root': {
													fontSize: 40,
												},
											}}
										/>
									}
									label={'여성'}
								/>
							</RadioGroup>
						</Stack>
					)}
				</Stack>
				<Stack
					flex={1}
					pt={2}
					sx={{ overflowY: 'auto' }}
					spacing={2}>
					<Typography
						textAlign={'center'}
						variant={'h6'}
						sx={{ whiteSpace: 'pre-line' }}>
						{
							'저희 매장은 안전 사고 예방과 미아 방지를 위해 \n 고객님의 개인정보를 활용하고 있습니다.'
						}
					</Typography>
					<TermsCondtionsDetailContentsSection />
					<TermsConditionsConsentsSection formik={formik} />
				</Stack>
				<PrevNextButton
					nextDisabled={!formik.isValid}
					prevFn={props.goPrevStep}
					nextFn={formik.handleSubmit}
					nextLabel='멤버십 가입 완료'
				/>
			</Stack>
		</UserInfoPageTemplate>
	);
};

const TermsConditionsConsentsSection = ({
	formik,
}: {
	formik: ReturnType<typeof useFormik<AgreementType>>;
}) => {
	const { values, setValues } = formik;

	const isAllChecked =
		values.kinderlabsAgreement && values.storeAgreement && values.marketingAgreement;

	const check = (targetField: 'kinderlabsAgreement' | 'storeAgreement' | 'marketingAgreement') => {
		setValues((prev) => ({
			...prev,
			[targetField]: !prev[targetField],
		}));
	};

	const checkAll = () => {
		setValues({
			birthday: values.birthday,
			gender: values.gender,
			kinderlabsAgreement: !isAllChecked,
			storeAgreement: !isAllChecked,
			marketingAgreement: !isAllChecked,
		});
	};

	return (
		<Stack
			flex={1}
			spacing={4}
			sx={{ width: '100%', height: '100%' }}>
			<CustomCheckBox
				type={'all'}
				autoFocus
				checked={isAllChecked}
				onChange={() => checkAll()}
			/>
			<Stack spacing={1}>
				<CustomCheckBox
					type={'store'}
					checked={values.storeAgreement}
					onChange={() => check('storeAgreement')}
				/>
				<CustomCheckBox
					type={'kinderlabs'}
					checked={values.kinderlabsAgreement}
					onChange={() => check('kinderlabsAgreement')}
				/>
				<CustomCheckBox
					type={'marketing'}
					checked={values.marketingAgreement}
					onChange={() => check('marketingAgreement')}
				/>
			</Stack>
		</Stack>
	);
};

const TermsCondtionsDetailContentsSection = () => {
	return (
		<Stack
			flex={1}
			spacing={4}
			sx={(theme) => ({
				width: '100%',
				height: '100%',
				justifyContent: 'center',
				bgcolor: theme.palette.grey['300'],
				borderRadius: 4,
				p: 2,
			})}>
			{termsConditionsDetailList.map((detail) => (
				<Stack key={detail.id}>
					{detail.title && <Typography variant='body2'>{detail.title}</Typography>}
					{detail.desc.map((line) => (
						<Typography
							variant='body2'
							key={line}>
							{line}
						</Typography>
					))}
				</Stack>
			))}
		</Stack>
	);
};

type TermsConditionsDetailListType = {
	id: number;
	title?: string;
	desc: string[];
};

const termsConditionsDetailList: TermsConditionsDetailListType[] = [
	{
		id: 1,
		desc: [
			'대기 서비스 제공을 위해 주식회사 킨더랩스 및 대기를 신청한 매장에서 다음과 같이 개인정보를 수집, 이용하고 있습니다.',
		],
	},
	{
		id: 2,
		title: ' - (필수) 만 14세 이상임을 확인합니다.',
		desc: [
			'만14세 미만 아동의 개인정보 보호를 위하여 만 14세 미만 아동의 이용을 제한하고 있습니다. 만14세 미만 아동은 본 서비스를 이용하실 수 없습니다.',
		],
	},
	{
		id: 3,
		title: ' - (필수) 킨더랩스의 개인정보 수집 및 이용 동의',
		desc: [
			'수집, 이용 항목 : 성명 / 휴대전화번호 / 생년월일 / 거주지 / 성별',
			'이용 목적 : 매장이용 대기 접수, 이용기록 보관 및 안내, 서비스 개선',
			'보유기간 : 최종 이용 후 2년간 보관',
		],
	},
	{
		id: 4,
		desc: [
			'필요한 최소한의 개인정보 수집에 동의하셔야 서비스를 이용하실 수 있으며, 동의 거부시 서비스 이용이 제한됩니다.',
		],
	},
];

const CustomCheckBox = ({
	checked,
	onChange,
	autoFocus = false,
	type,
}: {
	checked: boolean;
	onChange: () => void;
	type: 'all' | 'kinderlabs' | 'store' | 'marketing';
} & CheckboxProps) => {
	return (
		<Stack
			direction={'row'}
			alignItems={'center'}
			spacing={4}>
			<Checkbox
				autoFocus={autoFocus}
				disableFocusRipple
				checked={checked}
				icon={
					<CheckCircleOutlined
						type='message'
						style={{ fontSize: 40 }}
					/>
				}
				checkedIcon={
					<CheckCircleFilled
						type='message'
						style={{ fontSize: 40 }}
					/>
				}
				onChange={onChange}
			/>
			<Typography variant='h6'>{`${
				type === 'all' ? '' : TCAgreementContents[type].isRequired ? '(필수)' : '(선택)'
			} ${TCAgreementContents[type].title}`}</Typography>
			{type !== 'all' && (
				<WithDialog
					dialog={(open, closeDialog) => (
						<TermsConditionDetailDialog
							open={open}
							closeDialog={closeDialog}
							dialogTitle={TCAgreementContents[type].title}
							detailList={TCAgreementContents[type].list}
						/>
					)}>
					{(openDialog) => (
						<IconButton
							onClick={openDialog}
							color='primary'>
							<ExportOutlined />
						</IconButton>
					)}
				</WithDialog>
			)}
		</Stack>
	);
};

const TermsConditionDetailDialog = ({
	open,
	closeDialog,
	dialogTitle,
	detailList,
}: {
	open: boolean;
	closeDialog: () => void;
	dialogTitle: string;
	detailList: TermsConditionsType[];
}) => {
	return (
		<WithDialog.XlDialog
			// maxWidth={'xl'}
			// fullWidth
			fullScreen
			open={open}
			closeDialog={closeDialog}>
			<DialogTitle>
				<Stack
					direction={'row'}
					sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
					{dialogTitle}
					<IconButton
						size={'large'}
						onClick={closeDialog}>
						<CloseOutlined style={{ color: 'black' }} />
					</IconButton>
				</Stack>
			</DialogTitle>
			<DialogContent>
				<Stack
					flex={1}
					sx={{ overflow: 'auto' }}>
					{detailList.map((detail, idx) => (
						<Stack key={detail.title}>
							{detail.title && (
								<Stack spacing={1}>
									<Typography variant='subtitle1'>
										{idx + 1}. {detail.title}
									</Typography>
									<Divider sx={{ width: '100%' }} />
								</Stack>
							)}
							<ol>
								<Stack spacing={0.5}>
									{detail.infos.map((info, idx) => (
										<li key={idx}>
											<Stack key={info.title}>
												<Typography>{info.title}</Typography>
												{info.descs && (
													<ul>
														{info.descs.map((info, idx) => (
															<li>
																<Typography key={idx}>{info.label}</Typography>
															</li>
														))}
													</ul>
												)}
												{info.table &&
													getTCTable(info.table.tableHeadList, info.table.tableBodyList)}
											</Stack>
										</li>
									))}
								</Stack>
							</ol>
						</Stack>
					))}
				</Stack>
			</DialogContent>
			<DialogActions></DialogActions>
		</WithDialog.XlDialog>
	);
};

const getTCTable = (tableHeadList: string[], tableBodyList: string[][]) => {
	return (
		<TableContainer>
			<Table>
				<TableHead>
					<TableRow>
						{tableHeadList.map((th, idx) => (
							<TableCell key={idx}>
								<Typography>{th}</Typography>
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{tableBodyList.map((tb, idx) => (
						<TableRow key={idx}>
							{tb.map((tbdata, idx) => (
								<TableCell key={idx}>
									<Typography sx={{ whiteSpace: 'pre-line' }}>{tbdata}</Typography>
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

const StyledNumberButton = styled(Button)(({ theme }) => ({
	aspectRatio: '1/1',
	borderRadius: 8,
	border: `2px solid ${theme.palette.primary.main}`,
	minHeight: 70,
	fontSize: '1.5rem',
	fontWeight: 600,
	'&:hover': {
		borderWidth: '3px',
		transform: 'scale(1.02)',
		transition: 'all 0.2s ease-in-out',
	},
}));

export const NumberPinDialogContent = ({
	initialPinValues,
	onSubmit,
	closeDialog,
	errorMessage,
}: {
	initialPinValues: string;
	onSubmit: (pinValues: string) => void;
	closeDialog: () => void;
	errorMessage?: string;
}) => {
	const [pinValues, setPinValues] = useState<string>(initialPinValues);
	const hasError = !!errorMessage;

	// 생년월일 형식 검증: yyyyMMdd (8자리)
	const validateBirthday = (value: string): string | undefined => {
		if (!value) {
			return '생년월일을 입력해주세요';
		}
		const birthdayRegex = /^(19[0-9][0-9]|20\d{2})(0[0-9]|1[0-2])(0[1-9]|[1-2][0-9]|3[0-1])$/;
		if (!birthdayRegex.test(value)) {
			return '생년월일 형식에 맞지 않습니다. (예: 19900101)';
		}
		return undefined;
	};

	const validationError = validateBirthday(pinValues);
	const hasValidationError = !!validationError;
	const displayError = hasError ? errorMessage : validationError;
	const isSubmitDisabled = hasValidationError || pinValues.length === 0;

	const handleNumberClick = (number: number) => {
		if (pinValues.length < 8) {
			setPinValues((prev) => prev + number.toString());
		}
	};

	const handleDelete = () => {
		if (pinValues.length > 0) {
			setPinValues((prev) => prev.slice(0, -1));
		}
	};

	const handleClearAll = () => {
		setPinValues('');
	};

	return (
		<DialogContent sx={{ minWidth: 480, px: 3, py: 3, borderRadius: 16 }}>
			<Stack spacing={3}>
				{/* 입력된 값 표시 */}
				<Stack
					direction='row'
					spacing={1}
					sx={{
						p: 3,
						borderRadius: 3,
						border: '2px solid',
						minHeight: 80,
						alignItems: 'center',
						justifyContent: 'center',
						transition: 'all 0.2s ease-in-out',
					}}>
					<Typography
						variant='h4'
						sx={{
							letterSpacing: 3,
							fontWeight: 600,
							color: pinValues ? 'text.primary' : 'text.secondary',
						}}>
						{pinValues || '입력하세요'}
					</Typography>
				</Stack>

				{/* 에러 메시지 */}
				<Typography
					variant='body2'
					color={'error'}
					sx={{
						textAlign: 'center',
						px: 2,
						py: 1,
					}}>
					{displayError ?? 'ㅤ'}
				</Typography>

				{/* 번호 입력기 */}
				<Grid2
					container
					spacing={2.5}
					sx={{ mt: 1 }}>
					{/* 1-9 숫자 버튼 */}
					{[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
						<Grid2
							xs={4}
							key={num}>
							<StyledNumberButton
								variant='outlined'
								fullWidth
								onClick={() => handleNumberClick(num)}>
								<Typography variant='h4'>{num}</Typography>
							</StyledNumberButton>
						</Grid2>
					))}

					{/* 지우기 버튼 */}
					<Grid2 xs={4}>
						<StyledNumberButton
							variant='outlined'
							fullWidth
							onClick={handleDelete}>
							<BackspaceOutlinedIcon sx={{ fontSize: 36 }} />
						</StyledNumberButton>
					</Grid2>

					{/* 0 버튼 */}
					<Grid2 xs={4}>
						<StyledNumberButton
							variant='outlined'
							fullWidth
							onClick={() => handleNumberClick(0)}>
							<Typography variant='h4'>0</Typography>
						</StyledNumberButton>
					</Grid2>

					{/* 다지우기 버튼 */}
					<Grid2 xs={4}>
						<StyledNumberButton
							variant='outlined'
							fullWidth
							onClick={handleClearAll}>
							<DeleteOutlinedIcon sx={{ fontSize: 36 }} />
						</StyledNumberButton>
					</Grid2>
				</Grid2>

				<DialogActions sx={{ px: 0, pt: 2 }}>
					<Button
						onClick={closeDialog}
						variant='outlined'
						sx={{ minWidth: 100, px: 6 }}>
						취소
					</Button>
					<Button
						variant='contained'
						disabled={isSubmitDisabled}
						onClick={() => onSubmit(pinValues)}
						sx={{ minWidth: 100, flex: 1 }}>
						확인
					</Button>
				</DialogActions>
			</Stack>
		</DialogContent>
	);
};
